/**
 * Portal system — glowing arches scattered across the snow world.
 *
 * Main portals (4) — large, 28 m from spawn, cardinal directions.
 * Scattered portals (12) — smaller, 40–75 m, various angles.
 *
 * Rotation formula for any portal at (px, pz) to face toward spawn:
 *   rot = -Math.atan2(px, -pz)
 */

import { MeshBuilder }      from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { GlowLayer }        from "@babylonjs/core/Layers/glowLayer";
import { Color3 }           from "@babylonjs/core/Maths/math.color";
import { Vector3 }          from "@babylonjs/core/Maths/math.vector";
import { isDown }           from "../core/input.js";

const PORTAL_VIEW   = 32;   // HUD name appears within this distance (m)
const PORTAL_RADIUS = 7;    // E-to-enter radius (m)
const D             = 28;   // main portal distance from spawn

// ── Main portals ──────────────────────────────────────────────────────────────
const PORTALS_MAIN = [
    {
        id: "main", label: "צא למסע", sublabel: "liortesta.com", icon: "🏔️",
        url: "https://liortesta.com/",
        color: new Color3(0.55, 0.78, 0.93),
        pos: { x: 0, z: -D }, rotation: 0,
    },
    {
        id: "ai", label: "מסלול AI המקיף", sublabel: "50% הנחה ⚡", icon: "🤖",
        url: "https://liortesta.com/claude-code-course/",
        color: new Color3(0.0, 1.0, 0.53),
        pos: { x: D, z: -8 }, rotation: -Math.PI / 2,
    },
    {
        id: "shop", label: "לחנות", sublabel: "liortesta.com/shop", icon: "🛍️",
        url: "https://liortesta.com/shop/",
        color: new Color3(1.0, 0.82, 0.4),
        pos: { x: -D, z: -8 }, rotation: Math.PI / 2,
    },
    {
        id: "classic", label: "מצב קלאסי", sublabel: "HOME2", icon: "🎮",
        url: "https://liortesta.com/HOME2/",
        color: new Color3(0.8, 0.5, 1.0),
        pos: { x: 0, z: D }, rotation: Math.PI,
    },
];

// ── Scattered portals (pages + apps) — placed at 45–75 m, varied angles ──────
const _r = (px, pz) => -Math.atan2(px, -pz); // rotation formula

const PORTALS_EXTRA = [
    // ── Main Pages ──
    {
        id: "courses", label: "כל הקורסים", sublabel: "courses-page", icon: "📚",
        url: "https://liortesta.com/courses-page/",
        color: new Color3(0.98, 0.88, 0.2),
        pos: { x: 50, z: 8 },
    },
    {
        id: "coursemap", label: "מפת קורסים", sublabel: "course-map", icon: "🗺️",
        url: "https://liortesta.com/course-map/",
        color: new Color3(0.3, 0.85, 0.45),
        pos: { x: -42, z: -38 },
    },
    {
        id: "automation", label: "אוטומציה AI", sublabel: "automation", icon: "⚙️",
        url: "https://liortesta.com/automation/",
        color: new Color3(1.0, 0.55, 0.1),
        pos: { x: 22, z: 60 },
    },
    {
        id: "aiagents", label: "AI Agents", sublabel: "ai-agents", icon: "🧠",
        url: "https://liortesta.com/ai-agents/",
        color: new Color3(0.15, 0.72, 1.0),
        pos: { x: -60, z: 18 },
    },
    {
        id: "compareai", label: "השוואת AI", sublabel: "compare-ai-courses", icon: "📊",
        url: "https://liortesta.com/compare-ai-courses/",
        color: new Color3(1.0, 0.28, 0.5),
        pos: { x: 65, z: -28 },
    },
    {
        id: "aimodel", label: "מדריך מודלים", sublabel: "ai-model-guide", icon: "📖",
        url: "https://liortesta.com/ai-model-guide/",
        color: new Color3(0.65, 0.38, 1.0),
        pos: { x: -12, z: -68 },
    },
    {
        id: "travel", label: "וובינר תיירות", sublabel: "travel-webinar", icon: "✈️",
        url: "https://liortesta.com/travel-webinar/",
        color: new Color3(0.1, 0.82, 0.92),
        pos: { x: 48, z: -52 },
    },
    {
        id: "coursesguide", label: "מדריך קורסים", sublabel: "courses-guide", icon: "📋",
        url: "https://liortesta.com/courses-guide/",
        color: new Color3(0.82, 0.62, 0.18),
        pos: { x: -70, z: -10 },
    },
    // ── Apps & Games ──
    {
        id: "games", label: "כל המשחקים", sublabel: "games", icon: "🕹️",
        url: "https://liortesta.com/games/",
        color: new Color3(0.25, 1.0, 0.38),
        pos: { x: 72, z: 20 },
    },
    {
        id: "game", label: "מיני משחק", sublabel: "game", icon: "🎯",
        url: "https://liortesta.com/game/",
        color: new Color3(1.0, 0.62, 0.08),
        pos: { x: -50, z: 58 },
    },
    {
        id: "home3", label: "HOME3 עולמות", sublabel: "HOME3", icon: "🌍",
        url: "https://liortesta.com/HOME3/",
        color: new Color3(0.45, 0.92, 0.28),
        pos: { x: 18, z: 72 },
    },
    {
        id: "demo3d", label: "Demo 3D", sublabel: "demo-3d", icon: "🔮",
        url: "https://liortesta.com/demo-3d/",
        color: new Color3(0.92, 0.25, 0.92),
        pos: { x: -35, z: 68 },
    },
];

// Inject rotation into extra portals
for (const def of PORTALS_EXTRA) {
    def.rotation = _r(def.pos.x, def.pos.z);
}

export class PortalSystem {
    constructor(scene, terrain) {
        this._scene    = scene;
        this._terrain  = terrain;
        this._portals  = [];
        this._hint     = document.getElementById("portal-hint");
        this._hintTxt  = document.getElementById("portal-hint-name");
        this._hintKey  = document.getElementById("portal-hint-key");
        this._eWasDown = false;
        this._cooldown = 0;

        this._glow = new GlowLayer("portalGlow", scene, { blurKernelSize: 32 });
        this._glow.intensity = 0.8;

        this._build();
    }

    _build() {
        for (const def of PORTALS_MAIN) {
            const y = this._terrain.heightAt(def.pos.x, def.pos.z);
            const p = this._buildPortal(def, def.pos.x, y, def.pos.z, def.rotation, false);
            this._portals.push({ def, portal: p });
        }
        for (const def of PORTALS_EXTRA) {
            const y = this._terrain.heightAt(def.pos.x, def.pos.z);
            const p = this._buildPortal(def, def.pos.x, y, def.pos.z, def.rotation, true);
            this._portals.push({ def, portal: p });
        }
    }

    _buildPortal(def, px, py, pz, rot, small) {
        const scene = this._scene;
        const c = def.color;

        const pillarH = small ? 3.6  : 5.0;
        const pillarW = small ? 0.32 : 0.45;
        const archW   = small ? 2.4  : 3.2;
        const ringD   = small ? 4.0  : 5.5;

        const stoneMat = new StandardMaterial(`stone_${def.id}`, scene);
        stoneMat.diffuseColor  = new Color3(0.12, 0.14, 0.18);
        stoneMat.emissiveColor = c.scale(0.12);
        stoneMat.specularColor = new Color3(0.3, 0.3, 0.3);

        const glowMat = new StandardMaterial(`glow_${def.id}`, scene);
        glowMat.emissiveColor = c;
        glowMat.disableLighting = true;
        glowMat.alpha = 0.55;
        glowMat.backFaceCulling = false;

        const orbMat = new StandardMaterial(`orbM_${def.id}`, scene);
        orbMat.emissiveColor = c;
        orbMat.disableLighting = true;

        // Left pillar
        const pillarL = MeshBuilder.CreateBox(`pl_${def.id}`, {
            width: pillarW, height: pillarH, depth: pillarW
        }, scene);
        pillarL.position.set(px - archW / 2, py + pillarH / 2, pz);
        pillarL.material = stoneMat;
        pillarL.renderingGroupId = 1;

        // Right pillar
        const pillarR = MeshBuilder.CreateBox(`pr_${def.id}`, {
            width: pillarW, height: pillarH, depth: pillarW
        }, scene);
        pillarR.position.set(px + archW / 2, py + pillarH / 2, pz);
        pillarR.material = stoneMat;
        pillarR.renderingGroupId = 1;

        // Lintel
        const lintel = MeshBuilder.CreateBox(`lt_${def.id}`, {
            width: archW + pillarW * 2, height: small ? 0.35 : 0.5, depth: pillarW
        }, scene);
        lintel.position.set(px, py + pillarH + (small ? 0.175 : 0.25), pz);
        lintel.material = stoneMat;
        lintel.renderingGroupId = 1;

        // Top orb
        const orb = MeshBuilder.CreateSphere(`orb_${def.id}`, {
            diameter: small ? 0.48 : 0.65, segments: 8
        }, scene);
        orb.position.set(px, py + pillarH + (small ? 0.55 : 0.75), pz);
        orb.material = orbMat;
        orb.renderingGroupId = 1;
        this._glow.addIncludedOnlyMesh(orb);

        // Fill plane (energy inside arch)
        const fill = MeshBuilder.CreatePlane(`fill_${def.id}`, {
            width: archW - 0.1, height: pillarH - 0.2
        }, scene);
        fill.position.set(px, py + (pillarH - 0.2) / 2 + 0.1, pz);
        fill.rotation.y = rot + Math.PI;
        fill.material = glowMat;
        fill.renderingGroupId = 1;
        fill.isPickable = false;

        const fillB = fill.clone(`fillB_${def.id}`);
        fillB.rotation.y = rot;
        fillB.renderingGroupId = 1;
        fillB.isPickable = false;

        // Rune marks on pillars
        const runeCount = small ? 2 : 3;
        for (let i = 0; i < runeCount; i++) {
            const rune = MeshBuilder.CreateBox(`rune_${def.id}_${i}`, {
                width: 0.07, height: 0.22, depth: 0.07
            }, scene);
            rune.position.set(px - archW / 2, py + 1.0 + i * (small ? 1.0 : 1.2), pz + pillarW * 0.52);
            rune.material = orbMat;
            rune.renderingGroupId = 1;
            this._glow.addIncludedOnlyMesh(rune);

            const runeR = rune.clone(`runeR_${def.id}_${i}`);
            runeR.position.x = px + archW / 2;
            runeR.renderingGroupId = 1;
        }

        // Ground ring
        const ring = MeshBuilder.CreateTorus(`ring_${def.id}`, {
            diameter: ringD, thickness: 0.07, tessellation: 32
        }, scene);
        ring.position.set(px, py + 0.05, pz);
        ring.rotation.x = Math.PI / 2;
        const ringMat = new StandardMaterial(`ringM_${def.id}`, scene);
        ringMat.emissiveColor = c.scale(0.7);
        ringMat.disableLighting = true;
        ring.material = ringMat;
        ring.renderingGroupId = 1;
        this._glow.addIncludedOnlyMesh(ring);

        this._glow.addIncludedOnlyMesh(fill);
        this._glow.addIncludedOnlyMesh(fillB);

        return { fill, fillB, orb, ring, glowMat, orbMat, ringMat };
    }

    update(charPos, time, dt) {
        const pulse     = 0.55 + 0.45 * Math.sin(time * 2.2);
        const slowPulse = 0.7  + 0.3  * Math.sin(time * 0.7);

        let nearestPortal = null;
        let nearestDist   = Infinity;

        for (const { def, portal } of this._portals) {
            const dx   = charPos.x - def.pos.x;
            const dz   = charPos.z - def.pos.z;
            const dist = Math.sqrt(dx * dx + dz * dz);

            const activated = dist < PORTAL_RADIUS * 2;
            const p = activated ? pulse : slowPulse * 0.4 + 0.3;
            portal.glowMat.alpha       = p * 0.55;
            portal.orbMat.emissiveColor  = def.color.scale(0.6 + p * 0.6);
            portal.ringMat.emissiveColor = def.color.scale(p * (activated ? 0.8 : 0.3));

            if (dist < PORTAL_VIEW && dist < nearestDist) {
                nearestDist   = dist;
                nearestPortal = def;
            }
        }

        const eDown = isDown("KeyE");

        if (nearestPortal) {
            if (this._hint)    this._hint.classList.add("visible");
            if (this._hintTxt) this._hintTxt.textContent = `${nearestPortal.icon}  ${nearestPortal.label}`;
            if (this._hintKey) this._hintKey.style.opacity = nearestDist < PORTAL_RADIUS ? "1" : "0";

            if (eDown && !this._eWasDown && this._cooldown <= 0 && nearestDist < PORTAL_RADIUS) {
                this._cooldown = 1.5;
                window.open(nearestPortal.url, "_blank");
            }
        } else {
            if (this._hint) this._hint.classList.remove("visible");
        }

        this._eWasDown = eDown;
        if (this._cooldown > 0) this._cooldown -= dt;
    }
}
